import { ApiResponse } from '@models/apiResponse';
import { httpDelete, httpGet, httpPatch, httpPost } from './apiService';
import { OrderBy, OrderDirection } from '@models/requestFilters';
import { generateQueryFromIds } from '@helpers/querystring';

export abstract class EntityAdminApiService<Entity> {
    protected readonly baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    protected returnResult<T>(response: ApiResponse<T>): T | undefined {
        if (response) {
            if (response.success) {
                return response.data;
            } else if (response.message) {
                throw new Error(response.message);
            } else {
                return;
            }
        } else {
            return;
        }
    }

    protected getOrderByString(orderBy?: OrderBy, orderDirection?: OrderDirection): string {
        let orderByString = '';

        if (orderBy) {
            orderByString += `${orderBy}`;

            if (orderDirection) {
                orderByString += `/${orderDirection}`;
            }
        }

        return orderByString;
    }

    protected returnArrayResult<T>(response: ApiResponse<T[]>): T[] {
        const result = this.returnResult(response);
        if (result) {
            return result;
        } else {
            return [];
        }
    }

    public async getById(id: number): Promise<Entity | undefined> {
        const response = await httpGet(`${this.baseUrl}/id/${id}`);

        return this.returnResult(await response.json());
    }

    public async delete(id: number): Promise<void> {
        const response = await httpDelete(`${this.baseUrl}/${id}`);

        return this.returnResult<void>(await response.json());
    }

    public async update(entity: Entity): Promise<Entity | undefined> {
        const response = await httpPatch(this.baseUrl, JSON.stringify(entity));

        return this.returnResult(await response.json());
    }

    public async add(entity: Omit<Entity, 'id'>): Promise<Entity | undefined> {
        const response = await httpPost(this.baseUrl, JSON.stringify(entity));

        return this.returnResult(await response.json());
    }

    public async getValidIds(pageIds: number[]): Promise<number[]> {
        const query = generateQueryFromIds(pageIds);
        const response = query ? await httpGet(`${this.baseUrl}/valid-ids/?${query}`) : undefined;

        return this.returnArrayResult(await response?.json());
    }
}
